import { createPortal } from 'react-dom';
import { motion } from 'framer-motion';
import { RefreshCw, X } from 'lucide-react';

// Shared confirmation dialog for destructive or irreversible actions.
// Extracted from Integrations.tsx so every destructive control in the app
// guards through the same chrome (see also PeopleRoles "Revoke invite" and
// ProjectSettingsPanel "Remove member").
export interface ConfirmModalProps {
  title: string;
  body: string;
  confirmLabel: string;
  confirmClassName?: string;
  onConfirm: () => void;
  onCancel: () => void;
  loading?: boolean;
}

export function ConfirmModal({
  title, body, confirmLabel, confirmClassName, onConfirm, onCancel, loading,
}: ConfirmModalProps) {
  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onCancel} />
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        transition={{ duration: 0.15 }}
        className="relative bg-white dark:bg-gray-900 border border-gray-200 dark:border-white/10 rounded-2xl shadow-xl p-6 w-full max-w-sm z-10"
      >
        <button onClick={onCancel} aria-label="Cancel" className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 dark:hover:text-white focus-visible:ring-2 focus-visible:ring-primary/50 rounded">
          <X className="h-4 w-4" />
        </button>
        <h3 className="text-title text-gray-900 dark:text-white mb-2">{title}</h3>
        <p className="text-body text-gray-500 dark:text-gray-400 mb-5">{body}</p>
        <div className="flex gap-2 justify-end">
          <button onClick={onCancel} className="px-4 py-2 text-meta rounded-lg bg-gray-100 dark:bg-white/10 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-white/15 focus-visible:ring-2 focus-visible:ring-primary/50 transition-colors">
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={loading}
            className={`px-4 py-2 text-meta rounded-lg font-medium transition-colors disabled:opacity-50 focus-visible:ring-2 focus-visible:ring-primary/50 ${confirmClassName ?? 'bg-primary text-white hover:bg-primary/90'}`}
          >
            {loading ? <span className="flex items-center gap-1.5"><RefreshCw className="h-4 w-4 animate-spin" />Working…</span> : confirmLabel}
          </button>
        </div>
      </motion.div>
    </div>,
    document.body,
  );
}
